import { MailLanguage } from "../../emailLanguage";
import { ApiResult, getJson, postJson } from "../../restClient";

/**
 * The Art. 16 DSA notice endpoints - contract with
 * `backend/GrooveGrid.Api/Controllers/DsaController.cs`:
 *
 *   GET  /api/dsa/contact     -> DsaContactPoint   (Art. 11/12)
 *   POST /api/dsa/reports     -> DsaReportResponse (Art. 16, rate limited)
 *   POST /api/dsa/objections  -> 202, no body      (Art. 17 (3) (f), rate limited)
 *
 * All three are unauthenticated: the routes have to be usable without an account,
 * and the person needing the objection route is typically someone whose account
 * has just been suspended.
 */

/**
 * The categories of `DsaReportCategory` by name - the enum is persisted as text,
 * so these are identifiers and must not be translated.
 */
export type DsaCategory =
  | "IllegalContent"
  | "CopyrightInfringement"
  | "HateSpeechOrViolence"
  | "ChildSexualAbuseMaterial"
  | "PrivacyViolation"
  | "Other";

/** Order the categories are offered in. */
export const DSA_CATEGORIES: DsaCategory[] = [
  "IllegalContent",
  "CopyrightInfringement",
  "HateSpeechOrViolence",
  "PrivacyViolation",
  "ChildSexualAbuseMaterial",
  "Other",
];

/**
 * The one category that may be reported without a name and an address: Art. 16 (2)
 * DSA exempts notices concerning Art. 3 to 7 of Directive 2011/93/EU from the
 * contact-details requirement.
 */
export const ANONYMOUS_CATEGORY: DsaCategory = "ChildSexualAbuseMaterial";

export interface DsaReportFormValues {
  category: DsaCategory;
  /** Art. 16 (2) (b) - where the content is, in the reporter's own words. */
  contentReference: string;
  /** Art. 16 (2) (a) - why the reporter considers the content unlawful. */
  description: string;
  /** Optional for the exempt category, required otherwise. */
  reporterName: string;
  reporterEmail: string;
  /** Art. 16 (2) (c) - the declaration that the notice is accurate and complete. */
  goodFaithConfirmed: boolean;
}

export interface DsaReportResponse {
  /** Reference of the notice, quoted back so the reporter can cite it. */
  reportId: string;
  /** When the notice was received - the clock of Art. 16 (6) starts here. */
  receivedAt: string;
  /**
   * Whether the acknowledgement of Art. 16 (4) was queued. False for an anonymous
   * notice, which is the price of the Art. 16 (2) exemption.
   */
  acknowledged?: boolean;
}

/**
 * The point of contact of Art. 11 and Art. 12 DSA. Fetched rather than hardcoded
 * so this page and the mail sender cannot drift apart; the page still carries a
 * fallback, because a point of contact that vanishes on a failed call is none.
 */
export interface DsaContactPoint {
  contactEmail: string;
  noticePath: string;
  imprintUrl: string;
  /** ISO 639-1 codes of the languages we accept communications in. */
  languages: string[];
  /** Always false - Art. 12 (2) forbids making the contact depend on an account. */
  accountRequired: boolean;
}

/** What the affected user states against a decision (Art. 17 (3) (f) DSA). */
export interface DsaObjectionFormValues {
  /** The case id printed in the statement of reasons. */
  caseId: string;
  /** The address the statement of reasons was sent to. */
  email: string;
  /** What the user says against the decision. */
  statement: string;
}

interface DsaReportPayload extends DsaReportFormValues {
  /** Picks the template language of the acknowledgement and the decision. */
  language: MailLanguage;
}

/** Files the notice. Never throws. */
export const submitDsaReport = (
  values: DsaReportFormValues,
  language: MailLanguage,
): Promise<ApiResult<DsaReportResponse>> => {
  const payload: DsaReportPayload = {
    ...values,
    contentReference: values.contentReference.trim(),
    description: values.description.trim(),
    reporterName: values.reporterName.trim(),
    reporterEmail: values.reporterEmail.trim(),
    language,
  };

  return postJson<DsaReportResponse>("dsa/reports", payload);
};

/**
 * Reads the point of contact. Never throws; the page falls back to the
 * addresses in `externalLinks.ts` when this fails.
 */
export const fetchContactPoint = (): Promise<ApiResult<DsaContactPoint>> =>
  getJson<DsaContactPoint>("dsa/contact");

/**
 * Records an objection against a decision. A 404 means the case id and the address
 * do not belong together, which is the only gate on this route. Never throws.
 */
export const submitObjection = (
  values: DsaObjectionFormValues,
): Promise<ApiResult<unknown>> =>
  postJson<unknown>("dsa/objections", {
    caseId: values.caseId.trim(),
    email: values.email.trim(),
    statement: values.statement.trim(),
  });
